import { ShieldCheck } from 'lucide-react';
import { Card } from '@reflex/ui/components/ui/card';
import { PanelTitle, PageDescription } from '@reflex/ui/components/layout/Typography';
import { useAuth } from '@/hooks/auth/useAuth';
import { getAuthProviderIcon } from '@/lib/plugin-registry';
import type { AuthProviders } from '@/lib/api';

interface AuthProviderListProps {
  /**
   * Optional override for the providers to render. Defaults to the providers
   * advertised by `/auth/me` via {@link useAuth}. Stories pass an explicit
   * value to avoid depending on the auth context.
   */
  providers?: AuthProviders;
}

/**
 * Read-only list of OAuth providers currently enabled on this instance.
 * Surfaced on the admin Auth page below the email-domain allowlist so admins
 * can see at a glance which sign-in methods are available without leaving
 * the page. Provider configuration itself lives in environment variables and
 * is not editable from the UI.
 */
export function AuthProviderList({ providers: providersOverride }: AuthProviderListProps) {
  const auth = useAuth();
  const providers = providersOverride ?? auth.providers;
  const entries = Object.entries(providers);

  return (
    <Card className="space-y-4 p-4" data-testid="auth-provider-list">
      <div>
        <PanelTitle as="h2" size="sm">
          Sign-in providers
        </PanelTitle>
        <PageDescription className="mt-0.5 text-xs">
          OAuth providers configured for this instance. Configure these via environment variables on
          the server.
        </PageDescription>
      </div>

      {entries.length === 0 ? (
        <PageDescription className="text-xs">
          No authentication providers are configured. Set the relevant client ID and secret
          environment variables on the server to enable sign-in.
        </PageDescription>
      ) : (
        <ul className="divide-y rounded-md border" data-testid="auth-provider-list-items">
          {entries.map(([name, { label }]) => {
            const Icon = getAuthProviderIcon(name);
            return (
              <li
                key={name}
                className="flex items-center gap-3 px-3 py-2.5"
                data-testid={`auth-provider-row-${name}`}
              >
                <span className="flex size-8 shrink-0 items-center justify-center rounded-md border bg-muted/40">
                  {Icon ? (
                    <Icon className="icon-md" />
                  ) : (
                    <img
                      src={`/plugin-icons/plugin-icon-auth-${name}.png`}
                      alt=""
                      className="size-5 object-contain"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  )}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{label}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    OAuth · <span className="font-mono">{name}</span>
                  </div>
                </div>
                <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
                  <ShieldCheck className="icon-sm text-status-success" aria-hidden="true" />
                  Enabled
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
